import * as React from "react"
import { Row, Col } from "react-grid-system"
import { DeviceMetrics, useEdgeApi } from "../../edge/EdgeProvider"
import { AntennaOrientation, LocalOrientation } from "../../types"
import { HorizontalSpacer } from "../atoms/spacers"
import SatelliteSignalCard from "../organisms/overview/SatelliteSignalCard"
import RadarSignalCard from "../organisms/overview/RadarSignalCard"
import SystemMessagesCard from "../organisms/overview/SystemMessagesCard"
import WifiCard from "../organisms/overview/WifiCard"
import PositionCard from "../organisms/overview/PositionCard"
import TemperaturesCard from "../organisms/system/TemperaturesCard"
import FansCard from "../organisms/system/FansCard"
import TransmissionCard from "../organisms/overview/TransmissionCard"
import PageContainer from "../atoms/PageContainer"
import { DeepPartial } from "edge-proto/dist/edge/v1/metrics"
import { signalQuality } from "../../utils/signal-strength"
import { BeamListEntry } from "../../edge/device_gen/motion"
import { formatOrbitalSlot } from "../../utils/formatters"
import useSignalStrengthHistory from "../../hooks/useSignalStrengthHistory"

const currentOrientation = (metrics: DeviceMetrics): DeepPartial<AntennaOrientation> => {
  return {
    polarization: {
      value: metrics.useCurrentOrientationPolarization(),
      orientation: undefined,
    },
    azimuth: metrics.useCurrentOrientationAzimuth(),
    elevation: metrics.useCurrentOrientationElevation(),
  }
}

const targetOrientation = (metrics: DeviceMetrics): DeepPartial<AntennaOrientation> => {
  return {
    polarization: {
      value: metrics.useTargetOrientationPolarization(),
      orientation: undefined,
    },
    azimuth: metrics.useTargetOrientationAzimuth(),
    elevation: metrics.useTargetOrientationElevation(),
  }
}

const targetLocalOrientation = (metrics: DeviceMetrics): DeepPartial<LocalOrientation> => {
  return {
    alpha: metrics.useTargetLocalOrientationAlpha(),
    beta: metrics.useTargetLocalOrientationBeta(),
    gamma: metrics.useTargetLocalOrientationGamma(),
  }
}

const motorLocalOrientation = (metrics: DeviceMetrics): DeepPartial<LocalOrientation> => {
  return {
    alpha: metrics.useMotorLocalOrientationAlpha(),
    beta: metrics.useMotorLocalOrientationBeta(),
    gamma: undefined,
  }
}

const OTHER_SATELLITE_ANGLES = [-1, 10, 23]

const HomePage: React.FunctionComponent<{
  headerHeight: number
  beamSwitchModalOpen: boolean
  setBeamSwitchModalOpen: (open: boolean) => void
}> = ({ beamSwitchModalOpen, setBeamSwitchModalOpen }) => {
  const { metrics, config, uiConfig } = useEdgeApi()

  const transmissionEnabled = config.useTransmissionIntentEnabled("Transmission")

  const cOverN = metrics.useModemSignalStrength()
  const powerDetSignal = metrics.usePowerDetectorStrength()

  const noset = () => {
    return
  }

  const beams = metrics.useBeamList()
  const satelliteLongitude = metrics.useSatellitePositionLon()
  const beamIndex = config.useBeamInfoId("")?.value
  const selectedBeam = beams?.find((beam: BeamListEntry) => beam.id === beamIndex)

  const activeBeamIndex = metrics.useBeamInfoId()
  const activeBeam = beams?.find((beam: BeamListEntry) => beam.id === activeBeamIndex)
  const signalStrengthHistory = useSignalStrengthHistory()

  return (
    <>
      <PageContainer>
        <HorizontalSpacer spacing={4} />
        <Row>
          <Col>
            <SatelliteSignalCard
              currentOrientation={currentOrientation(metrics)}
              targetOrientation={targetOrientation(metrics)}
              targetLocalOrientation={targetLocalOrientation(metrics)}
              motorLocalOrientation={motorLocalOrientation(metrics)}
              cOverN={cOverN}
              powerDetSignal={powerDetSignal}
              signalQuality={cOverN ? signalQuality(cOverN) : 0}
              beams={beams}
              configuredBeam={selectedBeam ? [selectedBeam.name, selectedBeam.id] : undefined}
              activeBeam={activeBeam ? [activeBeam.name, activeBeam.id] : undefined}
              longitudeSatellite={formatOrbitalSlot(satelliteLongitude)}
              rf={{ rx: metrics.useCarrierCarrier() }}
              bandwidth={{ rx: metrics.useCarrierBandwidth() }}
              lo={{ rx: metrics.useCarrierRxLo(), tx: metrics.useCarrierTxLo() }}
              beamSwitchModalOpen={beamSwitchModalOpen}
              setBeamSwitchModalOpen={setBeamSwitchModalOpen}
            />
          </Col>
        </Row>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col>
            <TransmissionCard
              active={transmissionEnabled?.value ?? false}
              onToggle={transmissionEnabled?.setValue ?? noset}
              onApply={() => uiConfig.pushToDevice()}
            />
          </Col>
        </Row>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col>
            <RadarSignalCard
              motorLocalOrientation={motorLocalOrientation(metrics).alpha}
              targetOrientation={targetOrientation(metrics).azimuth}
              targetSatelliteName={selectedBeam?.name}
              currentOrientation={currentOrientation(metrics).azimuth}
              currentElevation={currentOrientation(metrics).elevation}
              otherSatelliteAngles={OTHER_SATELLITE_ANGLES}
              signalStrengthHistory={signalStrengthHistory}
            />
          </Col>
        </Row>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col>
            <PositionCard />
            <HorizontalSpacer spacing={2} />
          </Col>
          <Col>
            <WifiCard />
          </Col>
        </Row>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col>
            <HorizontalSpacer spacing={2} />
            <SystemMessagesCard />
            <HorizontalSpacer spacing={2} />
            <TemperaturesCard />
            <HorizontalSpacer spacing={2} />
            <FansCard />
          </Col>
        </Row>
        <HorizontalSpacer spacing={4} />
      </PageContainer>
    </>
  )
}

export default HomePage
